import {useCallback, useMemo, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {selectGuestsOfHonor} from '~/CreateFabricInvitation/reducers/details/selectors';
import {
  type GuestOfHonor,
  OverlayGuestsOfHonor,
} from '~/CreateFabricInvitation/routes/EventDetails/components/OverlayGuestsOfHonor';
import type {CreateFlowFeatureControllerToggleProps} from '~/CreateFabricInvitation/types/types';
import {scrollEventDetailsModuleIntoView} from '~/CreateFabricInvitation/utils/misc';
import {InvitationWidget} from '~/common/components/InvitationWidget/InvitationWidget';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {CrownIcon} from '~library/svg/v2/CrownIcon';
import {areAllValuesFalsy} from '~library/utils/utils';

import {eviteGold} from '~libSass/v2/_exports.sass';

export const GuestsOfHonorToggle = ({
  active,
  renderAs = 'block',
  hide = false,
}: CreateFlowFeatureControllerToggleProps) => {
  const store = useStore();
  const lastSavedGohArray: GuestOfHonor[] = useSelector(selectGuestsOfHonor);
  const [showOverlay, setShowOverlay] = useState(false);

  const handleClose = useCallback(() => {
    setShowOverlay(false);
    scrollEventDetailsModuleIntoView();
  }, []);

  const submitUpdatedGuestsOfHonor = useCallback(
    (arrToSubmit: GuestOfHonor[]) => {
      for (const goh of arrToSubmit) {
        goh.email = goh.email.toLowerCase();
      }
      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {guestsOfHonor: {update: true, guests: arrToSubmit}},
      });
    },
    [store]
  );

  const hasEmptyGoh = useMemo(
    () => lastSavedGohArray.every((goh: GuestOfHonor) => areAllValuesFalsy(goh)),
    [lastSavedGohArray]
  );

  const handleOpen = useCallback(() => {
    setShowOverlay((show) => !show);
    if (!hasEmptyGoh) {
      scrollEventDetailsModuleIntoView();
    }
  }, [hasEmptyGoh]);

  const subtitle = hasEmptyGoh
    ? 'Highlight the star of your event.'
    : lastSavedGohArray.map((goh: GuestOfHonor) => goh.name).join(' & ');

  return (
    <InvitationWidget.Toggle
      filled={active}
      editable
      title="Guest of Honor"
      subtitle={subtitle}
      qaId="guests-of-honor-toggle"
      onClick={handleOpen}
      startAdornment={<CrownIcon ratio={0.75} color={eviteGold} />}
      renderAs={renderAs}
      hide={hide}
      overlay={
        <OverlayGuestsOfHonor
          isOpen={showOverlay}
          onClose={handleClose}
          guestsOfHonor={lastSavedGohArray}
          onSubmit={submitUpdatedGuestsOfHonor}
        />
      }
    />
  );
};
